import { Outlet, Link, useNavigate } from "react-router-dom";
import { useAuthStore } from "../store/authStore";
import { useState, useRef, useEffect } from "react";

export default function Layout() {
  const { user, clearAuth } = useAuthStore();
  const navigate = useNavigate();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isAdminDropdownOpen, setIsAdminDropdownOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const adminDropdownRef = useRef<HTMLDivElement>(null);

  const handleLogout = () => {
    clearAuth();
    navigate("/login");
  };

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
      if (adminDropdownRef.current && !adminDropdownRef.current.contains(event.target as Node)) {
        setIsAdminDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Get user initials for avatar
  const getUserInitials = () => {
    if (!user?.email) return "?";
    return user.email.charAt(0).toUpperCase();
  };

  // Get display name or email prefix
  const getDisplayName = () => {
    if (!user?.email) return "User";
    return user.email.split("@")[0];
  };

  return (
    <>
      {/* Header */}
      <header className="bg-dark-800 border-b border-dark-border sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex justify-between items-center">
            <Link to="/dashboard" className="text-2xl font-bold text-primary-400 flex items-center gap-2">
              <span>🦞</span>
              <span className="hidden sm:inline">Clawplete</span>
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden md:flex items-center gap-6">
              <Link to="/dashboard" className="nav-link">
                Dashboard
              </Link>
              <Link to="/surveys" className="nav-link">
                Surveys
              </Link>
              <Link to="/deposit" className="nav-link text-green-400 hover:text-green-300">
                Deposit
              </Link>
              <Link to="/redeem" className="nav-link text-orange-400 hover:text-orange-300">
                Redeem
              </Link>
              {user?.role === "admin" && (
                <div className="relative" ref={adminDropdownRef}>
                  <button
                    onClick={() => setIsAdminDropdownOpen(!isAdminDropdownOpen)}
                    className="nav-link text-purple-400 hover:text-purple-300 flex items-center gap-1"
                  >
                    Admin
                    <svg
                      className={`w-4 h-4 transition-transform ${isAdminDropdownOpen ? "rotate-180" : ""}`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                  {isAdminDropdownOpen && (
                    <div className="absolute left-0 mt-2 w-56 bg-dark-800 border border-dark-border rounded-lg shadow-lg py-2 z-50">
                      <Link
                        to="/admin"
                        onClick={() => setIsAdminDropdownOpen(false)}
                        className="block px-4 py-2 text-sm text-purple-300 hover:bg-dark-700 hover:text-purple-200"
                      >
                        <span className="flex items-center gap-2">🏠 Dashboard</span>
                      </Link>
                      <div className="border-t border-dark-border my-1"></div>
                      <Link
                        to="/admin/users"
                        onClick={() => setIsAdminDropdownOpen(false)}
                        className="block px-4 py-2 text-sm text-gray-300 hover:bg-dark-700 hover:text-gray-100"
                      >
                        <span className="flex items-center gap-2">👥 Users</span>
                      </Link>
                      <Link
                        to="/admin/surveys"
                        onClick={() => setIsAdminDropdownOpen(false)}
                        className="block px-4 py-2 text-sm text-gray-300 hover:bg-dark-700 hover:text-gray-100"
                      >
                        <span className="flex items-center gap-2">📋 Surveys</span>
                      </Link>
                      <Link
                        to="/admin/transactions"
                        onClick={() => setIsAdminDropdownOpen(false)}
                        className="block px-4 py-2 text-sm text-gray-300 hover:bg-dark-700 hover:text-gray-100"
                      >
                        <span className="flex items-center gap-2">💰 Transactions</span>
                      </Link>
                      <Link
                        to="/admin/redemptions"
                        onClick={() => setIsAdminDropdownOpen(false)}
                        className="block px-4 py-2 text-sm text-gray-300 hover:bg-dark-700 hover:text-gray-100"
                      >
                        <span className="flex items-center gap-2">🎁 Redemptions</span>
                      </Link>
                      <Link
                        to="/admin/fees"
                        onClick={() => setIsAdminDropdownOpen(false)}
                        className="block px-4 py-2 text-sm text-gray-300 hover:bg-dark-700 hover:text-gray-100"
                      >
                        <span className="flex items-center gap-2">📊 Fee Summary</span>
                      </Link>
                    </div>
                  )}
                </div>
              )}

              {/* User Dropdown */}
              <div className="relative" ref={dropdownRef}>
                <button
                  onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                  className="flex items-center gap-2 nav-link"
                  data-testid="user-menu-button"
                >
                  <div className="w-8 h-8 rounded-full bg-primary-500/20 flex items-center justify-center text-primary-300 font-medium text-sm">
                    {getUserInitials()}
                  </div>
                  <span className="hidden lg:inline">{getDisplayName()}</span>
                  <svg
                    className={`w-4 h-4 transition-transform ${isDropdownOpen ? "rotate-180" : ""}`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>

                {isDropdownOpen && (
                  <div className="absolute right-0 mt-2 w-48 bg-dark-800 border border-dark-border rounded-lg shadow-lg py-1 z-50">
                    <div className="px-4 py-2 border-b border-dark-border">
                      <p className="text-sm font-medium text-gray-100 truncate">{user?.email}</p>
                      <p className="text-xs text-gray-500 capitalize">{user?.role?.replace("_", " ")}</p>
                    </div>
                    <Link
                      to="/deposit"
                      onClick={() => setIsDropdownOpen(false)}
                      className="block px-4 py-2 text-sm text-green-400 hover:bg-dark-700 hover:text-green-300"
                    >
                      <span className="flex items-center gap-2">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                        </svg>
                        Deposit
                      </span>
                    </Link>
                    <Link
                      to="/redeem"
                      onClick={() => setIsDropdownOpen(false)}
                      className="block px-4 py-2 text-sm text-orange-400 hover:bg-dark-700 hover:text-orange-300"
                    >
                      <span className="flex items-center gap-2">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        Redeem
                      </span>
                    </Link>
                    <div className="border-t border-dark-border my-1"></div>
                    <Link
                      to="/profile"
                      onClick={() => setIsDropdownOpen(false)}
                      className="block px-4 py-2 text-sm text-gray-300 hover:bg-dark-700 hover:text-gray-100"
                    >
                      <span className="flex items-center gap-2">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                        </svg>
                        Profile
                      </span>
                    </Link>
                    <button
                      onClick={() => {
                        setIsDropdownOpen(false);
                        handleLogout();
                      }}
                      data-testid="logout-button"
                      className="w-full text-left px-4 py-2 text-sm text-red-400 hover:bg-dark-700 hover:text-red-300"
                    >
                      <span className="flex items-center gap-2">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                        </svg>
                        Logout
                      </span>
                    </button>
                  </div>
                )}
              </div>
            </nav>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="md:hidden nav-link p-2"
              data-testid="mobile-menu-button"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                {isMobileMenuOpen ? (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                ) : (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                )}
              </svg>
            </button>
          </div>

          {/* Mobile Navigation */}
          {isMobileMenuOpen && (
            <nav className="md:hidden mt-4 pt-4 border-t border-dark-border space-y-2">
              <Link
                to="/dashboard"
                onClick={() => setIsMobileMenuOpen(false)}
                className="block py-2 text-gray-300 hover:text-gray-100"
              >
                Dashboard
              </Link>
              <Link
                to="/surveys"
                onClick={() => setIsMobileMenuOpen(false)}
                className="block py-2 text-gray-300 hover:text-gray-100"
              >
                Surveys
              </Link>
              <Link
                to="/deposit"
                onClick={() => setIsMobileMenuOpen(false)}
                className="block py-2 text-green-400 hover:text-green-300"
              >
                Deposit
              </Link>
              <Link
                to="/redeem"
                onClick={() => setIsMobileMenuOpen(false)}
                className="block py-2 text-orange-400 hover:text-orange-300"
              >
                Redeem
              </Link>
              <Link
                to="/profile"
                onClick={() => setIsMobileMenuOpen(false)}
                className="block py-2 text-gray-300 hover:text-gray-100"
              >
                Profile
              </Link>
              {user?.role === "admin" && (
                <div className="border-l-2 border-purple-500/30 pl-3 my-2">
                  <p className="text-purple-400 text-sm font-medium mb-1">Admin</p>
                  <Link
                    to="/admin"
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="block py-1 text-gray-300 hover:text-gray-100 pl-2"
                  >
                    Dashboard
                  </Link>
                  <Link
                    to="/admin/users"
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="block py-1 text-gray-300 hover:text-gray-100 pl-2"
                  >
                    Users
                  </Link>
                  <Link
                    to="/admin/surveys"
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="block py-1 text-gray-300 hover:text-gray-100 pl-2"
                  >
                    Surveys
                  </Link>
                  <Link
                    to="/admin/transactions"
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="block py-1 text-gray-300 hover:text-gray-100 pl-2"
                  >
                    Transactions
                  </Link>
                  <Link
                    to="/admin/redemptions"
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="block py-1 text-gray-300 hover:text-gray-100 pl-2"
                  >
                    Redemptions
                  </Link>
                  <Link
                    to="/admin/fees"
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="block py-1 text-gray-300 hover:text-gray-100 pl-2"
                  >
                    Fee Summary
                  </Link>
                </div>
              )}
              <button
                onClick={() => {
                  setIsMobileMenuOpen(false);
                  handleLogout();
                }}
                data-testid="mobile-logout-button"
                className="block w-full text-left py-2 text-red-400 hover:text-red-300"
              >
                Logout
              </button>
            </nav>
          )}
        </div>
      </header>

      {/* Main content */}
      <main className="min-h-screen bg-dark-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <Outlet />
        </div>
      </main>
    </>
  );
}
